import { atom, selector, selectorFamily } from 'recoil'

import { STORE_KEY_ID_LIST } from 'Constants'
import type {
  GroupItem,
  GroupStats,
  GroupsStatsState,
  ModalCopyState,
  ModalDeleteState,
  PaginationState,
  PanelEditState,
  SearchState,
} from 'Pages/GroupsList/model'
import type { ListOut } from 'Types/beListApi'
import type { LabelValuePair } from 'Types/common'
import { storeGet } from 'mailup-react-helpers'

export const paginationState = atom<PaginationState>({
  key: 'groupsList_paginationState',
  default: {
    pageNumber: 1,
    pageSize: 10,
    totalItems: 0,
    orderBy: 'Id',
    orderDescending: true,
    loading: false,
  },
})

export const searchState = atom<SearchState>({
  key: 'groupsList_searchState',
  default: {
    groupType: 'All',
    searchBy: '',
  },
})

export const resultsState = atom<GroupItem[]>({
  key: 'groupsList_resultsState',
  default: [],
})

export const selectionState = atom<GroupItem[]>({
  key: 'groupsList_selectionState',
  default: [],
})

export const selectedAllState = selector<boolean>({
  key: 'groupsList_selectedAllState',
  get: ({ get }) => {
    const results = get(resultsState)
    const selection = get(selectionState)

    const resultsIds = results.filter((r) => r.Deleteable).map((o) => o.Id)

    return (
      selection.length > 0 &&
      resultsIds.every((resultId) =>
        selection.some((selectedItem) => selectedItem.Id === resultId),
      )
    )
  },
})

export const selectedItemState = selectorFamily<boolean, number>({
  key: 'groupsList_selectedItemState',
  get:
    (idGroup) =>
    ({ get }) => {
      const selection = get(selectionState)

      return selection.some((selectedItem) => selectedItem.Id === idGroup)
    },
})

export const modalDeleteState = atom<ModalDeleteState>({
  key: 'groupsList_modalDeleteState',
  default: {
    show: false,
  },
})

export const panelEditState = atom<PanelEditState>({
  key: 'groupsList_panelEditState',
  default: {
    show: false,
    nameIsDirty: false,
    nameConflict: false,
  },
})

export const panelEditCanSaveState = selector({
  key: 'groupsList_panelEditCanSaveState',
  get: ({ get }) => {
    const { item, nameConflict } = get(panelEditState)

    return item !== undefined && item.Name.length > 0 && !nameConflict
  },
})

export const groupsStatsState = atom<GroupsStatsState>({
  key: 'groupsList_groupsStatsState',
  default: {
    stats: [],
    loading: false,
  },
})

export const defaultModalCopyState: ModalCopyState = {
  show: false,
  step: 'form',
  group: undefined,
  newList: undefined,
  newName: '',
  nameValid: true,
  nameValidLoading: false,
}

export const modalCopyState = atom<ModalCopyState>({
  key: 'groupsList_modalCopyState',
  default: defaultModalCopyState,
})

export const userVisibleListsState = atom<ListOut[]>({
  key: 'groupsList_userVisibleListsState',
  default: [],
})

export const listsOptionsState = selector<LabelValuePair[]>({
  key: 'groupsList_listsOptionsState',
  get: ({ get }) => {
    const currList = storeGet(STORE_KEY_ID_LIST)
    const userVisibleLists = get(userVisibleListsState)
    return userVisibleLists
      .filter((list) => list.IdList !== currList)
      .map<LabelValuePair>((list) => ({
        value: list.IdList,
        label: `${list.IdList} - ${list.Name}`,
      }))
  },
})

export const copyingGroupStatsSelector = selector<GroupStats | undefined>({
  key: 'groupsList_copyingGroupStatsSelector',
  get: ({ get }) => {
    const { stats, loading } = get(groupsStatsState)
    const { group } = get(modalCopyState)
    return loading || !group
      ? undefined
      : stats.find((s) => s.IdGroup === group.Id)
  },
})

export const modalCopyNameNotUniqueSelector = selector<boolean>({
  key: 'groupsList_modalCopyNameNotUniqueSelector',
  get: ({ get }) => {
    const { nameValid, nameValidLoading } = get(modalCopyState)
    return !nameValidLoading && !nameValid
  },
})

export const modalCopyNameInvalidSelector = selector<boolean>({
  key: 'groupsList_modalCopyNameInvalidSelector',
  get: ({ get }) => {
    const { newName } = get(modalCopyState)
    const nameNotUnique = get(modalCopyNameNotUniqueSelector)
    return !newName.length || nameNotUnique
  },
})

export const modalCopyDisableGoNextSelector = selector<boolean>({
  key: 'groupsList_modalCopyDisableGoNextSelector',
  get: ({ get }) => {
    const { step, nameValid, nameValidLoading, newList, newName } =
      get(modalCopyState)
    return (
      step === 'form' &&
      (nameValidLoading || !nameValid || !newList || !newName.length)
    )
  },
})
